import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiBell, FiClock, FiExternalLink, FiChevronLeft, FiChevronRight } = FiIcons;

const BreakingNews = () => {
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);

  // Mock breaking news data - in a real app, this would come from an API
  const breakingNews = [
    {
      id: 1,
      title: "President Trump Announces Major Policy Speech on Immigration",
      time: "2 hours ago",
      source: "Republican National Committee",
      url: "#",
      category: "Policy",
      relatedTruthPost: "The Border is BROKEN! My upcoming speech will outline how we'll FIX it on Day One. America FIRST means Americans are SAFE!"
    },
    {
      id: 2,
      title: "Supreme Court Rules 6-3 in Favor of Second Amendment Rights",
      time: "5 hours ago",
      source: "Constitutional Defense Foundation",
      url: "#",
      category: "Judicial",
      relatedTruthPost: "Big WIN for our Second Amendment! SCOTUS upholds our Constitutional Rights against the Radical Left's agenda. Many people are saying this is HUGE!"
    },
    {
      id: 3,
      title: "GOP Introduces New Bill to Protect American Energy Independence",
      time: "Yesterday",
      source: "Energy Freedom Coalition",
      url: "#",
      category: "Legislation",
      relatedTruthPost: "American Energy DOMINANCE is coming back! We'll be energy independent again - no more BEGGING other countries. Lower prices, more JOBS!"
    },
    {
      id: 4,
      title: "Republican Fundraising Surges Ahead of Upcoming Elections",
      time: "2 days ago",
      source: "GOP Finance Committee",
      url: "#",
      category: "Elections",
      relatedTruthPost: "Republicans raising RECORD amounts! The Silent Majority is speaking with their wallets. Red Wave Coming! MAGA!"
    }
  ];

  // Auto-rotate breaking news items
  useEffect(() => {
    let interval;
    if (autoPlay) {
      interval = setInterval(() => {
        setCurrentNewsIndex((prev) => (prev + 1) % breakingNews.length);
      }, 8000);
    }

    return () => clearInterval(interval);
  }, [breakingNews.length, autoPlay]);

  const goToPrevious = () => {
    setAutoPlay(false);
    setCurrentNewsIndex((prev) => (prev - 1 + breakingNews.length) % breakingNews.length);
  };

  const goToNext = () => {
    setAutoPlay(false);
    setCurrentNewsIndex((prev) => (prev + 1) % breakingNews.length);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="bg-gradient-to-r from-patriot-red to-patriot-navy text-white p-5 rounded-xl shadow-lg mb-6 max-w-4xl mx-auto relative overflow-hidden"
    >
      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M20 20l10-10v20l-10-10zm-10 0l10 10v-20l-10 10z'/%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      
      {/* Header */}
      <div className="flex justify-between items-center mb-4 relative">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
            <SafeIcon icon={FiBell} className="h-6 w-6 text-patriot-red animate-pulse" />
          </div>
          <h2 className="text-xl font-bold tracking-wide">BREAKING NEWS</h2>
        </div>
        <div className="flex items-center space-x-3">
          <span className="inline-block px-2 py-1 bg-white bg-opacity-20 backdrop-blur-sm rounded-full text-xs font-medium">
            {breakingNews[currentNewsIndex].category}
          </span>
          <div className="flex items-center text-xs text-white text-opacity-80">
            <SafeIcon icon={FiClock} className="h-3 w-3 mr-1" />
            <span>{breakingNews[currentNewsIndex].time}</span>
          </div>
        </div>
      </div>

      {/* Navigation controls */}
      <div className="absolute left-0 top-1/2 transform -translate-y-1/2 flex items-center justify-between w-full px-3 z-10 pointer-events-none">
        <button 
          onClick={goToPrevious}
          className="p-2 bg-black bg-opacity-30 rounded-full text-white hover:bg-opacity-50 transition-all duration-200 pointer-events-auto"
          aria-label="Previous news"
        >
          <SafeIcon icon={FiChevronLeft} className="h-4 w-4" />
        </button>
        <button 
          onClick={goToNext}
          className="p-2 bg-black bg-opacity-30 rounded-full text-white hover:bg-opacity-50 transition-all duration-200 pointer-events-auto"
          aria-label="Next news"
        >
          <SafeIcon icon={FiChevronRight} className="h-4 w-4" />
        </button>
      </div>

      {/* Content */}
      <div className="relative">
        {breakingNews.map((news, index) => (
          <motion.div
            key={news.id}
            initial={{ opacity: 0 }}
            animate={{ 
              opacity: currentNewsIndex === index ? 1 : 0,
              x: currentNewsIndex === index ? 0 : currentNewsIndex > index ? -20 : 20 
            }}
            transition={{ duration: 0.5 }}
            className={`${currentNewsIndex === index ? 'block' : 'hidden'}`}
          >
            <h3 className="text-lg sm:text-xl font-bold mb-3">
              {news.title}
            </h3>
            <div className="mb-2 text-sm md:text-base">
              <p className="text-white text-opacity-90 leading-relaxed">
                {news.relatedTruthPost && (
                  <span className="italic font-light">
                    Trump's Truth on this: "{news.relatedTruthPost}"
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-white text-opacity-80">
                Source: {news.source}
              </span>
              <a
                href={news.url}
                className="text-white text-opacity-90 hover:text-opacity-100 flex items-center text-sm bg-white bg-opacity-20 px-3 py-1 rounded-full hover:bg-opacity-30 transition-all duration-200"
              >
                Full Story
                <SafeIcon icon={FiExternalLink} className="h-3 w-3 ml-1" />
              </a>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pagination dots */}
      <div className="flex justify-center mt-4 space-x-2">
        {breakingNews.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setCurrentNewsIndex(index);
              setAutoPlay(false);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              currentNewsIndex === index ? 'bg-white w-4' : 'bg-white bg-opacity-50'
            }`}
            aria-label={`Go to news item ${index + 1}`}
          />
        ))}
      </div>
    </motion.div>
  );
};

export default BreakingNews;